import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    TextField,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    CircularProgress,
    Paper,
    InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CategoryIcon from "@mui/icons-material/Category";
import { toast } from "react-toastify";
import { REACT_APP_BACKEND_URL } from "../../../Utils/Language/urlBase";
import useAuthHeader from "react-auth-kit/hooks/useAuthHeader";
import { useParams } from "react-router";
import { useLang } from "../../../context/LanguageContext";
import { formatDate } from "../../../Utils/dateFormatter";

const StockLocationItems = () => {
    const { t } = useLang();
    const [stockItems, setStockItems] = useState([]);
    const [searchKey, setSearchKey] = useState("");
    const [loading, setLoading] = useState(false);
    const authHeader = useAuthHeader();
    const params = useParams();
    const { stockLocationId } = params;

    // Fetch Stock Location Items
    useEffect(() => {
        const fetchStockItems = async () => {
            setLoading(true);
            try {
                const response = await fetch(
                    `${REACT_APP_BACKEND_URL}/stock/stockLocation/getStockLocationItems/${stockLocationId}?searchKey=${searchKey}`,
                    {
                        headers: { Authorization: authHeader },
                    },
                );
                if (response.ok) {
                    const { items } = await response.json();
                    setStockItems(items);
                } else {
                    toast.error(
                        t("FAILED_TO_FETCH_DATA") ||
                            "Failed to fetch stock location items",
                    );
                }
            } catch (error) {
                toast.error(
                    t("NETWORK_ERROR") ||
                        "An error occurred while fetching items",
                );
            } finally {
                setLoading(false);
            }
        };
        fetchStockItems();
    }, [authHeader, searchKey, stockLocationId, t]);

    return (
        <Box sx={{ p: 3, width: "100%" }}>
            {/* Gradient Header */}
            <Paper
                elevation={0}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 3,
                    p: 2.5,
                    borderRadius: 3,
                    background:
                        "linear-gradient(135deg, #1e88e5 0%, #1565c0 100%)", // Blue gradient
                    color: "white",
                }}
            >
                <CategoryIcon sx={{ fontSize: 32, opacity: 0.9 }} />
                <Typography variant="h5" fontWeight={700}>
                    {t("STOCK_LOCATION_ITEMS")}
                </Typography>
            </Paper>

            {/* Search Field */}
            <TextField
                label={t("SEARCH")}
                variant="outlined"
                fullWidth
                margin="normal"
                value={searchKey}
                onChange={(e) => setSearchKey(e.target.value)}
                placeholder={t("SEARCH_ITEMS_PLACEHOLDER")}
                sx={{ backgroundColor: "white", borderRadius: 1 }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon color="action" />
                        </InputAdornment>
                    ),
                }}
            />

            {/* Loading Indicator */}
            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                // Items Table
                <TableContainer
                    component={Paper}
                    sx={{ mt: 2, borderRadius: 2, overflow: "hidden" }}
                >
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: "grey.50" }}>
                                <TableCell sx={{ fontWeight: 700 }}>
                                    {t("NAME")}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>
                                    {t("QUANTITY")}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>
                                    {t("EXPIRY_DATE")}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {stockItems.length > 0 ? (
                                stockItems.map((item) => (
                                    <TableRow key={item.id} hover>
                                        <TableCell>{item.item.name}</TableCell>
                                        <TableCell>
                                            {item.quantityRemaining}
                                        </TableCell>
                                        <TableCell>
                                            {formatDate(item.expiryDate)}
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell
                                        colSpan={3}
                                        align="center"
                                        sx={{ py: 3 }}
                                    >
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {t("NO_DATA") || "No items found"}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default StockLocationItems;
